import React from "react";
import { motion } from "framer-motion";
import { Zap, Shield, Eye } from "lucide-react";

const IMG =
  "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=80";

const VALUES = [
  {
    icon: Zap,
    title: "Efficiency",
    copy: "We streamline the entire business setup process — from license application to bank account opening — so your company is operational in the shortest possible time.",
  },
  {
    icon: Shield,
    title: "Integrity",
    copy: "We provide honest, transparent advice on jurisdiction selection, costs, and compliance requirements — helping you make informed decisions at every stage.",
  },
  {
    icon: Eye,
    title: "Transparency",
    copy: "We offer clear pricing, no hidden fees, and full visibility into your company formation progress from day one.",
  },
];

export default function WhoWeAreV2() {
  return (
    <section
      data-testid="v2-who-we-are"
      className="relative bg-white py-10 md:py-14"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative overflow-hidden rounded-sm">
            <img
              src={IMG}
              alt="Dubai business team"
              className="w-full h-[300px] md:h-[400px] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#002366]/30 to-transparent pointer-events-none" />
          </div>
          <div className="absolute -bottom-8 -right-8 bg-[#002366] text-white p-6 md:p-8 max-w-[280px] rounded-sm hidden md:block">
            <div className="font-heading font-light text-xl text-white leading-snug">
              Trusted partners for founders entering the{" "}
              <span className="font-black text-[#6B8AFF]">UAE market</span>.
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="overline text-[#3366FF]"
          >
            Who We Are
          </motion.p>
          <div className="gold-rule mt-5 mb-6" />
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight"
          >
            Reliable experts in business setup,{" "}
            <span className="font-black text-[#002366]">accounting & auditing</span>.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed"
          >
            StartINC is a trusted UAE corporate services firm specialising in
            comprehensive business solutions. Our highly skilled consultants have
            deep experience across finance, accounting, auditing, bookkeeping,
            company formation, trade licensing and corporate tax. We understand
            the unique needs of each client and engineer tailored solutions that
            let you achieve your goals faster.
          </motion.p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {VALUES.map(({ icon: Icon, title, copy }, i) => (
              <motion.div
                key={title}
                data-testid={`v2-value-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                className="p-6 rounded-sm border border-slate-200 bg-[#F8FAFC] hover:bg-white hover:shadow-md hover:border-[#3366FF]/40 transition-all"
              >
                <div className="w-11 h-11 rounded-sm bg-white border border-[#3366FF]/20 text-[#3366FF] inline-flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-heading font-semibold text-lg tracking-tight text-[#0F172A]">
                  {title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {copy}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
